import { Fragment, useCallback, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Checkbox } from "components/ui/checkbox";
import { Button } from "components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Skeleton } from "components/ui/skeleton";
import { toast as sonnerToast } from "sonner";
import { cn } from "utils";
import api from "apiSingleton";
import { GET_PERFECT_FIT_JOBS_FOR_USER } from "api/graphql/user";
import { JOB_APPLICATION_MATCH_STATUS } from "lookup";
import {
  getBullhornPublishedRates,
  isBullhornPublishedSalary,
} from "utils/common";
import { getPurifiedParsedMarkDown } from "utils/markdown";

const formatCurrencyDisplayValue = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "";
  }

  const absAmount = Math.abs(amount);

  if (absAmount >= 1000000) {
    return `$${Number((amount / 1000000).toFixed(1))}M`;
  }

  if (absAmount >= 1000) {
    return `$${Number((amount / 1000).toFixed(1))}k`;
  }

  return `$${amount}`;
};

const formatRateRangeDisplay = (min, max, unit, suffix) => {
  const minDisplay = formatCurrencyDisplayValue(min);
  const maxDisplay = formatCurrencyDisplayValue(max);
  const valueDisplay = minDisplay ? `${minDisplay}-${maxDisplay}` : maxDisplay;

  return `${valueDisplay}/${unit}${suffix ? ` (${suffix})` : ""}`;
};

const RateSalaryLine = ({ rateSalary }) => {
  if (!rateSalary) {
    return null;
  }

  return (
    <div className="leading-tight">
      <span className="block text-xs text-muted-foreground">
        {rateSalary.label}
      </span>
      <span className="whitespace-nowrap">{rateSalary.value}</span>
    </div>
  );
};

export function showToast({ text, type }) {
  sonnerToast.custom((id) => (
    <div
      id={id}
      className={cn("mx-auto rounded-md shadow-lg font-semibold px-3 py-1", {
        "text-green-500 bg-green-50": type === "success",
        "text-slate-500 bg-slate-50": type === "delete",
      })}
    >
      {text}
    </div>
  ));
}

export async function upsertMatch(userId, jobOpportunityId, isUnclaimed) {
  const applicationId = `User#${userId}`;
  const res = await api.match.get({
    applicationId,
    jobOpportunityId,
  });

  if (res.data.getMatch) {
    await api.match.update({
      input: {
        applicationId,
        jobOpportunityId,
        status: JOB_APPLICATION_MATCH_STATUS.MATCHED,
        ...(isUnclaimed ? { unclaimedWhenMatched: true } : {}),
      },
    });
  } else {
    await api.match.create({
      input: {
        jobOpportunityId,
        userId,
        status: JOB_APPLICATION_MATCH_STATUS.MATCHED,
        ...(isUnclaimed ? { unclaimedWhenMatched: true } : {}),
      },
    });
  }
}

export function PerfectFitJobHeader({
  allSelected,
  disabled,
  handleSelectAll,
}) {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>
          <Checkbox
            id="select-all-jobs"
            name="select-all-jobs"
            checked={!!allSelected}
            onCheckedChange={handleSelectAll}
            disabled={disabled}
          />
        </TableHead>
        <TableHead>Job Title</TableHead>
        <TableHead>Work Mode</TableHead>
        <TableHead>Location</TableHead>
        <TableHead>Rate / Salary</TableHead>
        <TableHead>Short Description</TableHead>
        <TableHead>Info</TableHead>
      </TableRow>
    </TableHeader>
  );
}

export function PerfectFitJobExpandedDetails({ details }) {
  return (
    <div className="rounded-md border bg-background p-4 shadow-sm">
      <p className="whitespace-pre-wrap break-words text-sm leading-6 text-foreground">
        {details ? (
          <span
            dangerouslySetInnerHTML={{
              __html: getPurifiedParsedMarkDown(details),
            }}
          />
        ) : (
          "No additional details were provided for this job."
        )}
      </p>
    </div>
  );
}

export function usePerfectFitJobs(userId) {
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [creatingMatches, setCreatingMatches] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);

  const [
    loadPerfectFitJobs,
    { data: jobsData, error: jobsError, loading: jobsLoading },
  ] = useLazyQuery(GET_PERFECT_FIT_JOBS_FOR_USER, {
    variables: { userId },
    fetchPolicy: "cache-and-network",
  });

  const perfectFitJobs = useMemo(
    () => (jobsError ? [] : jobsData?.listPerfectFitJobsForUser?.items || []),
    [jobsData, jobsError]
  );

  const getJobLocation = useCallback((job) => {
    if (job.location?.countryName) {
      return `${job.location.cityName}, ${job.location.stateName}, ${job.location.countryName}`;
    }

    if (job.geographicalRegions?.length > 0) {
      return `${job.geographicalRegions
        .map((gr) => gr.regionName)
        .join(", ")} Regions`;
    }

    return "";
  }, []);

  const getJobRateSalary = useCallback((job) => {
    if (job.maxRate?.value > 0) {
      return {
        label: "Customer",
        value: formatRateRangeDisplay(
          job.minRate?.value,
          job.maxRate.value,
          "hr"
        ),
      };
    }

    if (job.maxSalary?.value > 0) {
      return {
        label: "Customer",
        value: formatRateRangeDisplay(
          job.minSalary?.value,
          job.maxSalary.value,
          "yr"
        ),
      };
    }
  }, []);

  const getJobTalentRateSalary = useCallback((job) => {
    if (job.maxTalentRate?.value > 0) {
      return {
        label: "Talent",
        value: formatRateRangeDisplay(
          job.minTalentRate?.value,
          job.maxTalentRate.value,
          "hr"
        ),
      };
    }

    if (job.maxTalentSalary?.value > 0) {
      return {
        label: "Talent",
        value: formatRateRangeDisplay(
          job.minTalentSalary?.value,
          job.maxTalentSalary.value,
          "yr"
        ),
      };
    }

    const bullhornRates = getBullhornPublishedRates(job.metadata);

    if (bullhornRates.high?.value > 0) {
      const isSalary = isBullhornPublishedSalary(bullhornRates.high);

      return {
        label: isSalary ? "Talent Salary (BH)" : "Talent (BH)",
        value: formatRateRangeDisplay(
          bullhornRates.low?.value,
          bullhornRates.high.value,
          isSalary ? "yr" : "hr"
        ),
      };
    }
  }, []);

  const getPerfectJobFitReason = useCallback((details) => {
    let parsed;

    try {
      parsed = JSON.parse(details);
    } catch (err) {
      return "Could not parse reason successfully";
    }

    if (parsed?.content?.explanation) {
      return parsed.content?.explanation;
    }

    return "No reason provided";
  }, []);

  const handleSelectAll = useCallback(
    (checked) => {
      if (perfectFitJobs.length === 0) {
        return;
      }

      if (checked) {
        setSelectedJobs(
          new Set(perfectFitJobs.map((pfj) => pfj.jobOpportunity.id))
        );
      } else {
        setSelectedJobs(new Set());
      }
    },
    [perfectFitJobs]
  );

  const handleSelectRow = useCallback(
    (id, checked) => {
      const newSelectedJobs = new Set(selectedJobs);

      if (checked) {
        newSelectedJobs.add(id);
      } else {
        newSelectedJobs.delete(id);
      }

      setSelectedJobs(newSelectedJobs);
    },
    [selectedJobs]
  );

  const createMatchForJobs = useCallback(
    async (userId, isUnclaimed, onSuccess) => {
      if (selectedJobs.size === 0 || creatingMatches) {
        return;
      }

      setCreatingMatches(true);

      try {
        const matchCreationPromises = [];

        for (const jobId of selectedJobs) {
          matchCreationPromises.push(upsertMatch(userId, jobId, isUnclaimed));
        }

        const result = await Promise.allSettled(matchCreationPromises);
        const oneOrMoreFailed = result.some((r) => r.status === "rejected");

        if (oneOrMoreFailed) {
          showToast({
            text: "One or more matches could not be created. Try again later.",
            type: "error",
          });
          return;
        }

        showToast({ text: "Matches created successfully", type: "success" });
        setSelectedJobs(new Set());

        if (onSuccess) {
          onSuccess();
        }
      } catch (err) {
        console.error("Error creating matches:", err);
        showToast({
          text: "One or more matches could not be created. Try again later.",
          type: "error",
        });
      } finally {
        setCreatingMatches(false);
      }
    },
    [selectedJobs, creatingMatches]
  );

  return {
    selectedJobs,
    creatingMatches,
    expandedJobId,
    setExpandedJobId,
    perfectFitJobs,
    jobsLoading,
    jobsError,
    loadPerfectFitJobs,
    getJobLocation,
    getJobRateSalary,
    getJobTalentRateSalary,
    getPerfectJobFitReason,
    handleSelectAll,
    handleSelectRow,
    createMatchForJobs,
  };
}

export function PerfectFitJobsTable({
  perfectFitJobs,
  jobsLoading,
  selectedJobs,
  expandedJobId,
  setExpandedJobId,
  handleSelectAll,
  handleSelectRow,
  getJobLocation,
  getJobRateSalary,
  getJobTalentRateSalary,
  getPerfectJobFitReason,
}) {
  const allSelected =
    selectedJobs.size === perfectFitJobs.length && perfectFitJobs.length > 0;

  return (
    <Table>
      <PerfectFitJobHeader
        allSelected={allSelected}
        disabled={perfectFitJobs.length === 0}
        handleSelectAll={handleSelectAll}
      />

      <TableBody>
        {jobsLoading ? (
          <TableRow>
            <TableCell colSpan="7">
              <Skeleton className="h-6 bg-foreground/5" />
            </TableCell>
          </TableRow>
        ) : perfectFitJobs.length === 0 ? (
          <TableRow>
            <TableCell colSpan="7" className="text-center">
              No perfect fit jobs found for this user
            </TableCell>
          </TableRow>
        ) : (
          perfectFitJobs.map((pfj) => {
            const jobId = pfj.jobOpportunity.id;
            const isExpanded = expandedJobId === jobId;

            return (
              <Fragment key={jobId}>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      id={`row-${jobId}-checkbox`}
                      name={`row-${jobId}-checkbox`}
                      checked={selectedJobs.has(jobId)}
                      onCheckedChange={(checked) =>
                        handleSelectRow(jobId, !!checked)
                      }
                    />
                  </TableCell>
                  <TableCell>{pfj.jobOpportunity.title}</TableCell>
                  <TableCell>{pfj.jobOpportunity.workMode}</TableCell>
                  <TableCell>{getJobLocation(pfj.jobOpportunity)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <RateSalaryLine
                        rateSalary={getJobRateSalary(pfj.jobOpportunity)}
                      />
                      <RateSalaryLine
                        rateSalary={getJobTalentRateSalary(pfj.jobOpportunity)}
                      />
                    </div>
                  </TableCell>
                  <TableCell>{pfj.jobOpportunity.shortDescription}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto gap-1 px-0 py-0 font-medium text-primary hover:bg-transparent hover:text-primary/80"
                      onClick={() =>
                        setExpandedJobId((currentJobId) =>
                          currentJobId === jobId ? null : jobId
                        )
                      }
                    >
                      {isExpanded ? "Hide Details" : "More Details"}
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>

                {isExpanded && (
                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                    <TableCell colSpan="7" className="pt-0">
                      <PerfectFitJobExpandedDetails
                        details={getPerfectJobFitReason(
                          pfj.perfectJobFitDetails
                        )}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
